<template>
  <Teleport to="body">
    <Transition name="contact-modal-fade">
      <div
        v-if="visible"
        class="contact-modal-root"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
      >
        <div class="contact-modal-mask" @click="closeContactModal"></div>
        <div class="contact-modal-panel">
          <button
            type="button"
            class="contact-modal-close"
            aria-label="关闭"
            @click="closeContactModal"
          >
            ×
          </button>
          <div class="contact-modal-header">
            <span class="contact-modal-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12.5L10 17.5L19 7.5" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
            <h2 id="contact-modal-title">请留下您的联系方式</h2>
          </div>
          <form class="contact-modal-form" @submit.prevent="submitForm">
            <label class="contact-modal-field">
              <span>姓名</span>
              <input
                v-model.trim="form.name"
                type="text"
                placeholder="请填写"
                :disabled="submitting"
              />
            </label>
            <label class="contact-modal-field">
              <span>联系方式</span>
              <input
                v-model.trim="form.contact"
                type="text"
                placeholder="请填写"
                :disabled="submitting"
              />
            </label>
            <label class="contact-modal-field">
              <span>公司名称</span>
              <input
                v-model.trim="form.company"
                type="text"
                placeholder="请填写"
                :disabled="submitting"
              />
            </label>
            <div class="contact-modal-actions">
              <button type="submit" :disabled="submitting">
                {{ submitting ? '提交中...' : '确认提交' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { reactive, ref, watch, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useContactModal } from '@/composables/useContactModal'
import { submitContactEmail, submitCrmLead, PROJECT_MANAGEMENT_CONTACT_EMAIL } from '@/utils/submitContactEmail'

export default {
  name: 'ContactModal',
  setup() {
    const { visible, closeContactModal } = useContactModal()
    const route = useRoute()
    const submitting = ref(false)
    const form = reactive({
      name: '',
      contact: '',
      company: '',
    })

    const resetForm = () => {
      form.name = ''
      form.contact = ''
      form.company = ''
    }

    const onKeydown = (event) => {
      if (event.key === 'Escape' && visible.value) closeContactModal()
    }

    watch(visible, (open) => {
      document.body.style.overflow = open ? 'hidden' : ''
      if (open) {
        window.addEventListener('keydown', onKeydown)
      } else {
        resetForm()
        window.removeEventListener('keydown', onKeydown)
      }
    })

    onBeforeUnmount(() => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKeydown)
    })

    const submitForm = async () => {
      if (!form.name || !form.contact) {
        ElMessage({ message: '请填写姓名和联系方式', type: 'warning' })
        return
      }

      submitting.value = true
      const fromProjectManagement = route.path === '/projectManagement'
      try {
        await Promise.all([
          submitContactEmail({
            to: fromProjectManagement ? PROJECT_MANAGEMENT_CONTACT_EMAIL : undefined,
            subject: fromProjectManagement ? '【官网】项目管理页预约演示' : '【官网】联系我们弹窗留言',
            name: form.name,
            contact: form.contact,
            company: form.company || '未填写',
            messageLines: [
              fromProjectManagement
                ? '收到一条项目管理页的预约演示留言，详情如下：'
                : '收到一条新的官网联系我们留言，详情如下：',
              '',
              `姓名：${form.name}`,
              `联系方式：${form.contact}`,
              `公司名称：${form.company || '未填写'}`,
              '',
              `提交时间：${new Date().toLocaleString('zh-CN', { hour12: false })}`,
            ],
          }),
          submitCrmLead({
            leadsName: form.company || '未填写',
            telephone: form.contact,
            remark: `客户姓名：${form.name}`,
          }),
        ])

        ElMessage({
          message: '提交成功，我们将在第一时间与您联系！',
          type: 'success',
        })
        closeContactModal()
      } catch (error) {
        closeContactModal()
      } finally {
        submitting.value = false
      }
    }

    return {
      visible,
      closeContactModal,
      form,
      submitting,
      submitForm,
    }
  },
}
</script>

<style scoped>
.contact-modal-root {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.contact-modal-mask {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.42);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}
.contact-modal-panel {
  position: relative;
  z-index: 1;
  width: min(640px, 100%);
  padding: 36px 40px 32px;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 28px 80px rgba(16, 24, 40, 0.18);
  user-select: text;
}
.contact-modal-close {
  position: absolute;
  top: 16px;
  right: 18px;
  width: 32px;
  height: 32px;
  color: #98a2b3;
  font-size: 24px;
  line-height: 1;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.contact-modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 28px;
}
.contact-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, #2f8cff 0%, #1677ff 100%);
  flex-shrink: 0;
}
.contact-modal-header h2 {
  margin: 0;
  color: #101828;
  font-size: clamp(22px, 2.2vw, 28px);
  font-weight: 700;
  letter-spacing: -.02em;
}
.contact-modal-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.contact-modal-field {
  display: grid;
  grid-template-columns: 88px 1fr;
  align-items: center;
  gap: 16px;
}
.contact-modal-field span {
  color: #344054;
  font-size: 16px;
  font-weight: 500;
}
.contact-modal-field input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  color: #101828;
  font-size: 15px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  outline: none;
  transition: border-color .2s ease, box-shadow .2s ease;
}
.contact-modal-field input:focus {
  border-color: #1677ff;
  box-shadow: 0 0 0 3px rgba(22, 119, 255, .12);
}
.contact-modal-field input::placeholder {
  color: #98a2b3;
}
.contact-modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}
.contact-modal-actions button {
  min-width: 148px;
  height: 46px;
  padding: 0 28px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #4aa3ff 0%, #1677ff 100%);
  cursor: pointer;
  transition: opacity .2s ease, transform .2s ease;
}
.contact-modal-actions button:hover:not(:disabled) {
  transform: translateY(-1px);
}
.contact-modal-actions button:disabled {
  opacity: .65;
  cursor: not-allowed;
}
.contact-modal-fade-enter-active,
.contact-modal-fade-leave-active {
  transition: opacity .24s ease;
}
.contact-modal-fade-enter-active .contact-modal-panel,
.contact-modal-fade-leave-active .contact-modal-panel {
  transition: transform .24s ease, opacity .24s ease;
}
.contact-modal-fade-enter-from,
.contact-modal-fade-leave-to {
  opacity: 0;
}
.contact-modal-fade-enter-from .contact-modal-panel,
.contact-modal-fade-leave-to .contact-modal-panel {
  opacity: 0;
  transform: translateY(12px) scale(.98);
}
@media (max-width: 767px) {
  .contact-modal-panel {
    padding: 28px 22px 24px;
    border-radius: 20px;
  }
  .contact-modal-field {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .contact-modal-actions {
    justify-content: stretch;
  }
  .contact-modal-actions button {
    width: 100%;
  }
}
</style>
